import { Button } from '@/components/ui/Button';
import type { SignupTerm } from '@/features/auth/types/signup-terms';
import { useModalEffects } from '@/shared/hooks/use-modal-effects';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Full-page Terms Detail modal for signup terms [보기].
 * Matches the Admin Web prototype's standalone terms page (terms.html).
 * Rendered as a fixed overlay by SignupTermsConsent — the signup form stays
 * mounted behind it, preserving all form state.
 */
interface TermsDetailPageProps {
	term: SignupTerm;
	onBack: () => void;
}

// ISO `YYYY-MM-DD` → `YYYY.MM.DD` (matches the settings terms viewer, TermsViewPage).
const formatIsoToDot = (iso: string): string => iso.slice(0, 10).replace(/-/g, '.');

export function TermsDetailPage({ term, onBack }: TermsDetailPageProps) {
	const { t } = useTranslation('auth');
	const modalRef = useRef<HTMLDivElement>(null);

	const handleBack = useCallback(() => {
		onBack();
	}, [onBack]);

	useModalEffects({
		open: true,
		onClose: handleBack,
		modalRef,
	});

	return (
		<div
			ref={modalRef}
			className='fixed inset-0 z-[300] overflow-y-auto bg-[var(--background)] p-[var(--space-6)]'
		>
			<div className='terms-page'>
				{/* Header bar */}
				<div className='terms-page__bar'>
					<Button
						type='button'
						variant='ghost'
						className='terms-page__back'
						onClick={handleBack}
						autoFocus
					>
						<ArrowLeft className='size-4' /> {t('signup.terms.back')}
					</Button>
					<h1>{term.name}</h1>
				</div>

				{/* Term section */}
				<div className='terms-page__section'>
					<h2>
						{term.name}{' '}
					{term.required && (
						<span className='terms-page__tag terms-page__tag--req'>
							{t('signup.terms.required')}
						</span>
					)}
					</h2>
					<p className='terms-page__meta'>
						{t('signup.terms.version')} {term.version} ·{' '}
						{t('signup.terms.effectiveDate')}{' '}
						{formatIsoToDot(term.effectiveDate)}
					</p>
					<div
						className='terms-page__body'
						dangerouslySetInnerHTML={{ __html: term.content }}
					/>
				</div>
			</div>
		</div>
	);
}
